import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import OutlinedInput from "@mui/material/OutlinedInput";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { getCookie, sanitizeData } from "../utils/Common";
import { ObjectType } from "../types/tableTypes";
import { LoansContext } from "../context/LoansContext";
import { getPricingAdjustmentAuditFieldPath } from "../constants/auditFields";
import AuditTag from "./common/AuditTag";

// "Final Price Adjustment" is the one row that's a computed sum, not an
// independently overridable adjustment — kept read-only even in edit mode,
// matching how LeverageTable already special-cases it for bolding/coloring.
const TOTAL_ROW_LABEL = "Final Price Adjustment";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgba(158, 156, 156, 0.1)",
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StrippedTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "rgba(158, 156, 156, 0.1)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "#FAFCFF",
  },
}));

const FilledTableRow = styled(TableRow)(({ theme }) => ({
  borderRadius: "6px",
  background: "#F5F8FF",
}));

const useStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: "none",
  },
}));

interface LeverageTableProps {
  tableHeader: any[];
  tableBody: ObjectType;
  label: string;
  tableClassName?: string;
  // Only ever passed for the "Pricing" adjustments card — every other
  // LeverageTable consumer (Leverages, LeveragesAdjustments, ...) is
  // unaffected by these. Edit-mode itself (the pencil/save/cancel icons) is
  // owned by the parent (rendered in the card header, alongside the title —
  // see CustomCard's headingActions) so it can sit on the same line as
  // "Pricing"; this component just renders inputs when told to.
  editable?: boolean;
  overrides?: Record<string, string>;
  isEditingCard?: boolean;
  draftValues?: Record<string, string>;
  onDraftChange?: (rowLabel: string, newValue: string) => void;
}

const LeverageTable: React.FC<LeverageTableProps> = ({
  tableHeader,
  tableBody,
  label,
  tableClassName,
  editable,
  overrides,
  isEditingCard,
  draftValues,
  onDraftChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const orgFundingType = getCookie("orgFundingType");
  // Same audit-tag convention as every other audited field (GetFieldUI.tsx):
  // a row only shows the history icon once auditedFieldPaths (fetched once
  // per loan load) actually contains its path.
  const { auditedFieldPaths } = useContext(LoansContext) ?? {};

  // Whether an override VALUE should be preferred for display is completely
  // independent of whether THIS viewer is allowed to edit it — a user
  // without update_pricing_adjustment must still see a manually-overridden
  // row's value, not the raw guideline-API one, they just can't change it.
  // Gating display behind `editable` (as canEditRows below does, correctly,
  // for the input/edit-icon UI) was the bug: it silently hid every override
  // from anyone without edit rights, showing the un-overridden API value
  // instead — exactly as if no override had ever been saved.
  const isPricingTable = label === "pricing" && Array.isArray(tableBody) && Array.isArray(tableBody[0]);
  const canEditRows = editable && isPricingTable;

  const getResultText = (key: string, parentKey: ObjectType, idx: number) => {
    let className = "";

    if (`${key}`.includes("-") && (!isNaN(+key) || `${key}`.includes("%"))) {
      className = "pass-text";
    } else if (`${key}`.includes("+") && (!isNaN(+key) || `${key}`.includes("%"))) {
      className = "failed-texts";
    }

    const showColouredTexts =
      (parentKey?.data?.length === 3 && parentKey?.data[1] === key) ||
      parentKey?.label === "requested";

    let keyToBeShown = key;
    if (Boolean(keyToBeShown) && /[+-]/.test(keyToBeShown)) {
      if (parentKey?.label?.toLowerCase() === "requested") {
        keyToBeShown = keyToBeShown.replace(/[+-]/g, "");
      }
    }

    let displayValue;
    if (`${keyToBeShown}`.includes("%")) {
      displayValue = keyToBeShown;
    } else if (typeof keyToBeShown === "string" && idx !== 0) {
      displayValue = sanitizeData(t(`${keyToBeShown}`), true, 2);
    } else {
      displayValue = sanitizeData(t(keyToBeShown), true);
    }

    return (
      <span className={showColouredTexts ? className : ""}>
        {displayValue}
      </span>
    );
  };

  const renderTableBody = useMemo(() => {
    if (!tableBody) return null;

    const getInterestRateLabel = (data: any, idx: number) => {
      return data === "actualInterestRate" && orgFundingType === "table_funding" && idx === 0
        ? <>Note Rate</>
        : t(data);
    }

    if (!Array.isArray(tableBody[0])) {
      return Object.keys(tableBody).map((key) => (
        <StrippedTableRow key={key}>
          {tableBody[key]?.data?.map((data: any, index: number) => (
            <StyledTableCell
              style={{
                color: "black",
                borderBottom: "none",
                marginBottom: "8px",
                borderRight: "1px solid #D5DCED",
              }}
              key={data}
              className={index === 0 ? tableBody[key].borderColor : ""}
            >
              {getResultText(data, tableBody[key], index)}
            </StyledTableCell>
          ))}
        </StrippedTableRow>
      ));
    } else {
      return tableBody.map((body: any, index: number) => (
        <React.Fragment key={body?.[0]}>
          <FilledTableRow
            style={
              body[0] === "Final Price Adjustment"
                ? { borderRadius: "6px", background: "#E4EAFF" }
                : {}
            }
          >
            {body.map((data: any, idx: number) => (
              <StyledTableCell
                style={
                  idx === 0 || body.length === 3
                    ? {
                        color: "black",
                        borderBottom: "none",
                        marginBottom: "8px",
                      }
                    : {
                        color: "black",
                        borderBottom: "none",
                        marginBottom: "8px",
                        display: "flex",
                        justifyContent: "end",
                      }
                }
                key={data}
                sx={{ borderBottom: "none" }}
              >
                <span
                  style={
                    idx !== 0
                      ? {
                          color: "var(--primary-primary-main-700, #40128B)",
                        }
                      : {}
                  }
                  className={
                    data &&
                    idx !== 0 &&
                    typeof data === "string" &&
                    data.includes("-") &&
                    label !== "pricing"
                      ? "red-color"
                      : ""
                  }
                >
                  {index === 0 && body[0] === "Final Price Adjustment" ? (
                        <b>{idx === 0  ? t("totalPricingAdjustment") : t(data)}</b>
                      ) : canEditRows &&
                        isEditingCard &&
                        idx !== 0 &&
                        body[0] !== TOTAL_ROW_LABEL ? (
                        <OutlinedInput
                          size="small"
                          value={draftValues?.[body[0]] ?? ""}
                          onChange={(e) => onDraftChange?.(body[0], e.target.value)}
                          sx={{ maxWidth: 120, background: "#fff" }}
                        />
                      ) : isPricingTable && idx === 0 && body[0] !== TOTAL_ROW_LABEL ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {getInterestRateLabel(data, idx)}
                          {auditedFieldPaths?.includes(
                            getPricingAdjustmentAuditFieldPath(body[0]),
                          ) ? (
                            <AuditTag
                              field={getPricingAdjustmentAuditFieldPath(body[0])}
                            />
                          ) : null}
                        </span>
                      ) : (
                        getInterestRateLabel(
                          isPricingTable && idx !== 0 && overrides?.[body[0]] !== undefined
                            ? overrides[body[0]]
                            : data,
                          idx,
                        )
                      )}
                </span>
              </StyledTableCell>
            ))}
          </FilledTableRow>
        </React.Fragment>
      ));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableBody, t, label, canEditRows, isPricingTable, isEditingCard, draftValues, overrides, auditedFieldPaths]);

  return (
    <Paper className={classes.paper}>
      <TableContainer
        sx={{
          padding: "0px 0px",
          borderRadius: "8px",
          fontSize: "1.1rem",
        }}
      >
        <Table className={tableClassName}>
          {tableHeader?.length ? (
            <TableHead
              style={{
                backgroundColor: "#9E9C9C1A",
              }}
            >
              <StrippedTableRow style={{ borderRadius: "8px" }}>
                {tableHeader.map((ele) => (
                  <StyledTableCell
                    className={ele?.headerClassName ?? ""}
                    key={ele?.label}
                  >
                    {t(ele?.label ?? ele)}
                  </StyledTableCell>
                ))}
              </StrippedTableRow>
            </TableHead>
          ) : null}
          <TableBody>{renderTableBody}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default React.memo(LeverageTable);
